import { useEffect, useState } from "react";

import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";

import { CircleCheckIcon } from "@lucide/icons";
import { differenceInMonths, formatDistanceToNow } from "date-fns";

import { formatDateRelative } from "simplydo/core/dates";
import type { OpenAPI } from "simplydo/interfaces";
import type { UserMe } from "simplydo/interfaces/types/userTypes";
import { useTheme } from "simplydo/react/theme";
import { Container } from "simplydo/react/ui/container";
import { Grid, GridColumn } from "simplydo/react/ui/grid";
import { H1, H4 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Input } from "simplydo/react/ui/input";
import { Loader } from "simplydo/react/ui/loader";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import NotFound from "components/NotFound";
import { TagChooserContent } from "components/lib/Choosers/Tags/TagModalChooser";
import RowComponent from "components/lib/RowComponent";
import { SideNav } from "components/lib/SideNav";
import { Banner } from "components/lib/UI";

import {
  type TagExplorerChallenge,
  type TagExplorerData,
  type TagExplorerIdea,
  type TagExplorerUser,
  emptyTagExplorerData,
} from "./types";

const Tags = () => {
  const user = useAppSelector((state) => state.user as UserMe);
  const ownerOrganisation = user.ownerOrganisation;
  const navigate = useNavigate();

  const params = useParams();
  const tagIds: string[] = params.ids ? params.ids.split(",") : [];
  const allTags = ownerOrganisation?.tagList ?? [];
  const theme = useTheme();

  const [loading, setLoading] = useState(false);
  const [tagData, setTagData] = useState<TagExplorerData>(emptyTagExplorerData);
  const currentTags = tagData.tags;
  const [searchValue, setSearchValue] = useState("");

  const { t } = useTranslation();

  useEffect(() => {
    const _tagIds = params.ids?.split(",") || [];
    setLoading(true);
    if (_tagIds.length > 0) {
      api.tags.full(
        _tagIds,
        (newTagData) => {
          setTagData(newTagData as TagExplorerData);
          setLoading(false);
        },
        () => setLoading(false),
      );
    } else {
      setTagData(emptyTagExplorerData());
      setLoading(false);
    }
  }, [params.ids]);

  if (!ownerOrganisation) {
    return <NotFound />;
  }

  if (!loading && !tagData) {
    return <NotFound />;
  }

  const { collections = {} } = tagData;
  const { ideas = [], challenges = [], users = [] } = collections;
  const combinedLength = Object.values(collections).reduce<number>(
    (total, collection) =>
      total + (Array.isArray(collection) ? collection.length : 0),
    0,
  );

  return (
    <div>
      <Helmet title={"Tags"} />

      <Banner marginless>
        <Container
          style={{
            paddingTop: 10,
            height: theme.sizes.isMobile ? 100 : undefined,
          }}
        >
          <Grid stackable columns={16}>
            <GridColumn span={16} computer={8}>
              <H1 style={{ color: "#3b3b3b", marginBottom: 2 }}>
                Tag explorer
              </H1>
              {tagIds.length && !theme.sizes.isMobile ? (
                <p
                  style={{
                    marginBottom: theme.sizes.isMobile ? "-20px" : undefined,
                  }}
                >
                  Results for{" "}
                  {tagData.tags
                    .slice(0, 3)
                    .map((tag) => `"${tag.value}"`)
                    .join(", ")}{" "}
                  {tagIds.length > 3
                    ? `and ${tagIds.length - 3} other(s).`
                    : ""}
                </p>
              ) : (
                <p
                  style={{
                    marginBottom: theme.sizes.isMobile ? "-20px" : undefined,
                  }}
                >
                  Find all tagged documents
                </p>
              )}
            </GridColumn>
          </Grid>
        </Container>
      </Banner>
      <SideNav
        nav={
          <div style={{ marginTop: 10, padding: 15 }}>
            <div style={{ marginBottom: 25 }}>
              <H4>Search</H4>
              <Input
                iconPosition="left"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder="Search..."
              />
            </div>
            <TagChooserContent
              tags={currentTags as OpenAPI.Schemas["Tag"][]}
              allTags={allTags}
              showCount
              addTag={(tag) => {
                const updatedTags = [...tagIds, tag._id];
                navigate(`/tags/${updatedTags.join(",")}`);
              }}
              removeTag={(tag) => {
                const index = tagIds.findIndex((tagId) => tagId === tag._id);
                if (index !== -1) {
                  const updatedTags = [...tagIds];
                  updatedTags.splice(index, 1);
                  navigate(`/tags/${updatedTags.join(",")}`);
                }
              }}
            />
          </div>
        }
        main={
          <>
            {loading && <Loader />}
            {!loading && !tagIds.length ? (
              <>
                <H1>No tags selected</H1>
                <p>
                  Use the sidebar filter to the right to search through tagged
                  items
                </p>
              </>
            ) : null}
            {!loading && tagIds.length && combinedLength === 0 ? (
              <>
                <H1>No results</H1>
                <p>Sorry, but we could not find any matching results.</p>
              </>
            ) : null}
            {!loading && combinedLength !== 0 ? (
              <>
                <RowComponent>
                  <H1>Ideas ({ideas.length})</H1>
                  {ideas
                    .filter((idea: TagExplorerIdea) =>
                      idea.name
                        .toLowerCase()
                        .includes(searchValue.toLowerCase()),
                    )
                    .map((idea: TagExplorerIdea, index: number) => (
                      <RowComponent.Cell index={index} key={idea._id} asChild>
                        <Link to={`/ideas/${idea._id}`}>
                          <div
                            style={{
                              width: 50,
                              height: 40,
                              borderRadius: 3,
                              backgroundSize: "cover",
                              backgroundPosition: "center center",
                              backgroundImage: `url(${util.mixinCssUrlFallback(util.ideaCoverImage(idea), util.ideaCoverImage(undefined))})`,
                            }}
                          >
                            {idea.isSubmitted && (
                              <Icon
                                icon={CircleCheckIcon}
                                color="green"
                                style={{ top: -5, right: -5 }}
                              />
                            )}
                          </div>
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              marginLeft: 5,
                              marginRight: 5,
                              position: "relative",
                              top: -1,
                            }}
                          >
                            <RowComponent.Title
                              style={{ color: "#5d5d5d" }}
                              block
                            >
                              {idea.name}
                            </RowComponent.Title>
                            <RowComponent.StatusArea>
                              {idea.isSubmitted ? (
                                <>
                                  <RowComponent.StatusLabel color="green" />
                                  Submitted{" "}
                                  {formatDateRelative(idea.submittedAt, {
                                    addSuffix: true,
                                  })}
                                </>
                              ) : (
                                <>
                                  <RowComponent.StatusLabel color="blue" />
                                  Updated{" "}
                                  {formatDateRelative(
                                    new Date(idea.updatedAt),
                                    { addSuffix: true },
                                  )}
                                </>
                              )}
                            </RowComponent.StatusArea>
                          </div>
                        </Link>
                      </RowComponent.Cell>
                    ))}
                </RowComponent>
                <RowComponent>
                  <H1>
                    {t("common:capitalise", { key: "generic.challenges" })} (
                    {challenges.length})
                  </H1>
                  {challenges
                    .filter((challenge: TagExplorerChallenge) =>
                      challenge.name
                        .toLowerCase()
                        .includes(searchValue.toLowerCase()),
                    )
                    .map((challenge: TagExplorerChallenge, index: number) => (
                      <RowComponent.Cell
                        index={index}
                        key={challenge._id}
                        asChild
                      >
                        <Link to={`/challenges/${challenge._id}`}>
                          <div
                            style={{
                              width: 50,
                              height: 40,
                              borderRadius: 3,
                              backgroundSize: "cover",
                              backgroundPosition: "center center",
                              backgroundImage: `url(${util.mixinCssUrlFallback(challenge.coverImage ?? "", util.challengeImage(undefined))})`,
                            }}
                          />
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              marginLeft: 5,
                              marginRight: 5,
                              position: "relative",
                              top: -1,
                            }}
                          >
                            <RowComponent.Title
                              style={{ color: "#5d5d5d" }}
                              block
                            >
                              {challenge.name}
                            </RowComponent.Title>
                            <RowComponent.StatusArea>
                              {challenge.stage === "closed" ? (
                                <>
                                  <RowComponent.StatusLabel color="red" />{" "}
                                  Closed
                                </>
                              ) : (
                                challenge.stage === "published" &&
                                (challenge.deadline ? (
                                  <>
                                    <RowComponent.StatusLabel color="blue" />{" "}
                                    Closes{" "}
                                    {formatDistanceToNow(
                                      new Date(challenge.deadline),
                                      { addSuffix: true },
                                    )}
                                  </>
                                ) : (
                                  <>
                                    <RowComponent.StatusLabel color="green" />{" "}
                                    Open
                                  </>
                                ))
                              )}
                            </RowComponent.StatusArea>
                          </div>
                        </Link>
                      </RowComponent.Cell>
                    ))}
                </RowComponent>
                {util.hasPermission(
                  user,
                  "org.viewDashboard",
                  ownerOrganisation._id,
                ) ? (
                  <RowComponent $noBorder>
                    <H1>Users ({users.length})</H1>
                    {users
                      .filter((filterUser: TagExplorerUser) =>
                        (filterUser.profile.fullName || "")
                          .toLowerCase()
                          .includes(searchValue.toLowerCase()),
                      )
                      .map((filterUser: TagExplorerUser, index: number) => (
                        <RowComponent.Cell
                          index={index}
                          key={filterUser._id}
                          asChild
                        >
                          <Link to={`/users/${filterUser._id}`}>
                            <div
                              style={{
                                width: 50,
                                height: 40,
                                borderRadius: 3,
                                backgroundSize: "cover",
                                backgroundPosition: "center center",
                                backgroundImage: `url(${util.mixinCssUrlFallback(util.avatarUrl(filterUser), util.avatarUrl())})`,
                              }}
                            />
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                marginLeft: 5,
                                marginRight: 5,
                                position: "relative",
                                top: -1,
                              }}
                            >
                              <RowComponent.Title
                                style={{ color: "#5d5d5d" }}
                                block
                              >
                                {filterUser.profile.fullName}
                              </RowComponent.Title>
                              <RowComponent.StatusArea>
                                {differenceInMonths(
                                  new Date(),
                                  new Date(filterUser.lastSeenAt),
                                ) >= 1 ? (
                                  <>
                                    <RowComponent.StatusLabel color="red" />
                                    {formatDistanceToNow(
                                      new Date(filterUser.lastSeenAt),
                                      { addSuffix: true },
                                    )}
                                  </>
                                ) : (
                                  <>
                                    <RowComponent.StatusLabel color="green" />
                                    {formatDistanceToNow(
                                      new Date(filterUser.lastSeenAt),
                                      { addSuffix: true },
                                    )}
                                  </>
                                )}
                              </RowComponent.StatusArea>
                            </div>
                          </Link>
                        </RowComponent.Cell>
                      ))}
                  </RowComponent>
                ) : null}
              </>
            ) : null}
          </>
        }
      />
    </div>
  );
};

export default Tags;
